/* ============================================================
   CUSTOM HEADER — Blossom Cases
   Written from scratch. Does not extend or depend on Dawn's
   MenuDrawer / HeaderMenu / DetailsModal classes.

   Dawn still owns two things and they keep working untouched:
   - the cart drawer, via the <a id="cart-icon-bubble"> contract
   - predictive search, via the <predictive-search> element that
     lives inside our own overlay shell
   ============================================================ */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  var scrollLocks = 0;

  function lockScroll() {
    scrollLocks += 1;
    document.body.classList.add('ch-locked');
  }

  function unlockScroll() {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.body.classList.remove('ch-locked');
  }

  /* Minimal focus trap — keeps Tab inside `container` until released. */
  function createTrap(container) {
    var previous = null;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var items = Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    return {
      activate: function (focusTarget) {
        previous = document.activeElement;
        document.addEventListener('keydown', onKeydown);
        if (focusTarget) window.setTimeout(function () { focusTarget.focus(); }, 60);
      },
      release: function (restoreFocus) {
        document.removeEventListener('keydown', onKeydown);
        if (restoreFocus !== false && previous && previous.focus) previous.focus();
        previous = null;
      },
    };
  }

  /* ----------------------------------------------------------
     Announcement bar — rotating messages
     ---------------------------------------------------------- */
  class ChAnnounce extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.index = 0;
      this.speed = parseInt(this.dataset.speed, 10) || 4000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      var key = this.dataset.dismissKey;
      if (key) {
        try {
          if (window.sessionStorage.getItem(key) === '1') {
            this.hidden = true;
            this.classList.add('is-dismissed');
            return;
          }
        } catch (e) {
          /* private mode — just show the bar */
        }
      }

      var closeBtn = this.querySelector('[data-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => {
          this.classList.add('is-dismissed');
          this.stop();
          if (key) {
            try {
              window.sessionStorage.setItem(key, '1');
            } catch (e) {
              /* nothing to persist to; the bar still closes for this view */
            }
          }
        });
      }

      var prev = this.querySelector('[data-prev]');
      var next = this.querySelector('[data-next]');
      if (prev) prev.addEventListener('click', () => { this.show(this.index - 1); this.start(); });
      if (next) next.addEventListener('click', () => { this.show(this.index + 1); this.start(); });

      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());

      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    show(i) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
    }

    start() {
      this.stop();
      if (!this.autoplay || this.reduced) return;
      this.timer = window.setInterval(() => this.show(this.index + 1), this.speed);
    }

    stop() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  /* ----------------------------------------------------------
     Header — sticky behaviour + compact-on-scroll
     ---------------------------------------------------------- */
  class ChHeader extends HTMLElement {
    connectedCallback() {
      this.bar = this.querySelector('[data-header-bar]');
      this.mode = this.dataset.sticky || 'always';
      this.last = window.scrollY;
      this.locked = false;

      this.publishBottom();
      window.addEventListener('resize', this.publishBottom.bind(this), { passive: true });

      if (this.mode === 'none') return;

      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
    }

    /* The mega panel caps its own height against the header's bottom edge. */
    publishBottom() {
      var bottom = Math.round(this.getBoundingClientRect().bottom);
      document.documentElement.style.setProperty('--ch-header-bottom', bottom + 'px');
    }

    /* Set by <ch-nav> while a mega panel is open, so the header can't
       slide away from under an open menu. */
    setLocked(value) {
      this.locked = value;
      if (value) this.classList.remove('is-hidden');
    }

    onScroll() {
      var y = window.scrollY;
      var height = this.offsetHeight;

      this.classList.toggle('is-stuck', y > 4);

      if (this.mode === 'scroll-up' && !this.locked) {
        this.classList.toggle('is-animating', y > height);
        if (y > this.last && y > height) {
          this.classList.add('is-hidden');
        } else if (y < this.last) {
          this.classList.remove('is-hidden');
        }
      }

      this.last = y;
      this.publishBottom();
    }
  }

  /* ----------------------------------------------------------
     Desktop nav — mega panels
     Hover-intent for mouse, click/tap for touch & keyboard.
     ---------------------------------------------------------- */
  class ChNav extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-nav-item]'));
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.openItem = null;
      this.closeTimer = null;

      this.items.forEach((item) => {
        var trigger = item.querySelector('[data-nav-trigger]');
        if (!trigger) return;

        trigger.addEventListener('click', (event) => {
          event.preventDefault();

          // Opening always works, whatever the input device — a tap on a
          // touchscreen never fires mouseenter first, so it must not rely
          // on hover having already opened the panel.
          if (!item.classList.contains('is-open')) {
            this.open(item);
            return;
          }

          // It's already open. With a mouse that's because hovering opened
          // it on the way to the click, so closing here would make the menu
          // flash shut; hover handles closing instead. Touch and keyboard
          // have no hover, so for them the click has to toggle.
          if (!this.finePointer.matches || event.detail === 0) this.close(item);
        });

        item.addEventListener('mouseenter', () => {
          if (!this.finePointer.matches) return;
          window.clearTimeout(this.closeTimer);
          this.open(item);
        });

        item.addEventListener('mouseleave', () => {
          if (!this.finePointer.matches) return;
          this.closeTimer = window.setTimeout(() => this.close(item), 160);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !this.openItem) return;
        var trigger = this.openItem.querySelector('[data-nav-trigger]');
        this.close(this.openItem);
        if (trigger) trigger.focus();
      });

      this.addEventListener('focusout', (event) => {
        if (!this.openItem) return;
        if (!this.openItem.contains(event.relatedTarget)) this.close(this.openItem);
      });

      this.onDocumentClick = (event) => {
        if (this.openItem && !this.contains(event.target)) this.close(this.openItem);
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    header() {
      return this.closest('ch-header');
    }

    open(item) {
      if (this.openItem && this.openItem !== item) this.close(this.openItem);
      item.classList.add('is-open');
      var trigger = item.querySelector('[data-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
      this.openItem = item;

      var header = this.header();
      if (header && header.setLocked) header.setLocked(true);
    }

    close(item) {
      item.classList.remove('is-open');
      var trigger = item.querySelector('[data-nav-trigger]');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;

      if (!this.openItem) {
        var header = this.header();
        if (header && header.setLocked) header.setLocked(false);
      }
    }

    toggle(item) {
      if (item.classList.contains('is-open')) {
        this.close(item);
      } else {
        this.open(item);
      }
    }
  }

  /* ----------------------------------------------------------
     Mobile drawer — slide-in with a two-level drilldown
     ---------------------------------------------------------- */
  class ChDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-drawer-panel]');
      this.scrim = this.querySelector('[data-drawer-scrim]');
      this.track = this.querySelector('[data-drawer-track]');
      this.level2 = this.querySelector('[data-level2]');
      this.trap = createTrap(this.panel);
      this.isOpen = false;

      this.openers = Array.from(document.querySelectorAll('[data-drawer-open]'));
      this.openers.forEach((btn) => btn.addEventListener('click', () => this.open()));

      this.querySelectorAll('[data-drawer-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());

      // Level 1 → level 2
      this.querySelectorAll('[data-drilldown]').forEach((btn) => {
        btn.addEventListener('click', () => this.showLevel2(btn.getAttribute('data-drilldown')));
      });

      // level 2 → back
      this.querySelectorAll('[data-drawer-back]').forEach((btn) =>
        btn.addEventListener('click', () => this.showLevel1())
      );

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.panel.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-open');
      this.panel.removeAttribute('aria-hidden');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      lockScroll();
      this.trap.activate(this.panel.querySelector(FOCUSABLE));
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.panel.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-open');
      this.panel.setAttribute('aria-hidden', 'true');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      this.trap.release();
      window.setTimeout(() => this.showLevel1(), 320);
    }

    showLevel2(key) {
      if (!this.level2) return;
      this.level2.querySelectorAll('[data-group]').forEach((group) => {
        group.hidden = group.getAttribute('data-group') !== key;
      });
      this.track.classList.add('is-level2');
      var back = this.level2.querySelector('[data-group]:not([hidden]) [data-drawer-back]');
      if (back) window.setTimeout(() => back.focus(), 120);
    }

    showLevel1() {
      if (this.track) this.track.classList.remove('is-level2');
    }
  }

  /* ----------------------------------------------------------
     Search overlay — our shell around Dawn's <predictive-search>
     ---------------------------------------------------------- */
  class ChSearch extends HTMLElement {
    connectedCallback() {
      this.sheet = this.querySelector('[data-search-sheet]');
      this.input = this.querySelector('input[type="search"]');
      this.trap = createTrap(this);
      this.isOpen = false;

      this.openers = Array.from(document.querySelectorAll('[data-search-open]'));
      this.openers.forEach((btn) => btn.addEventListener('click', () => this.open()));

      this.querySelectorAll('[data-search-close]').forEach((btn) =>
        btn.addEventListener('click', () => this.close())
      );

      var scrim = this.querySelector('[data-search-scrim]');
      if (scrim) scrim.addEventListener('click', () => this.close());

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.classList.add('is-open');
      this.removeAttribute('aria-hidden');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      lockScroll();
      this.trap.activate(this.input);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.openers.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      this.trap.release();
    }
  }

  customElements.define('ch-announce', ChAnnounce);
  customElements.define('ch-header', ChHeader);
  customElements.define('ch-nav', ChNav);
  customElements.define('ch-drawer', ChDrawer);
  customElements.define('ch-search', ChSearch);
})();
